import React from 'react';
import { Avatar } from '@framework-controls/core';
import * as css from './AppointmentCard.module.scss';
import { Appointment, Transportation } from 'src/models';
import { strings } from 'src/localization';
import { ViewMode } from 'src/enums';
import { DateTime } from '@reynolds/datetime';
import classNames from 'classnames';
import NoProfile from 'src/content/icons/Profile2.svg';
import { IconProfile2 } from 'src/content/icons';
import moment from 'moment';
import 'moment/min/locales.min';

export interface IProps {
    advisorPhotoPath: string;
    appointment: Appointment;
    advImgAltTag: string;
    viewMode?: ViewMode;
    theme: string;
    transIndex?: Transportation;
    locale: string;
    modifyAppointment?: () => void;
}

const AppointmentCard = (props: IProps) => {
    const appointment: Appointment = props.appointment;
    const dateValue = new DateTime(new Date(props.appointment.date));
    const displayDate = dateValue.format('DD, YYYY');
    const displayTime = props.appointment.time;
    const acctMode = props.viewMode === ViewMode.CustAcct;
    const editApptMode = props.viewMode === ViewMode.EditAppt;

    const processingDate = dateValue.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dayOfWeekLit = processingUTCDate.format('dddd');
    const MonthLit = processingUTCDate.format('MMMM');

    let flexBoxClassName = classNames(
        css.flexBox,
        {
            [css.flexBoxAcct]: acctMode,
        },
        {
            [css.flexBoxMini]: props.theme === 'mini',
        },
        {
            [css.flexBoxCadillac]: props.theme === 'cadillac',
        },
    );
    const dateRowClassName = classNames(
        css.dateRow,
        {
            [css.dateRowAcct]: acctMode,
            [css.dateRowKia]: props.theme === 'kia',
        },
    );
    // PMR 1462985 requested that we always underline the link
    let linkButtonClassName = css.summaryEditLinkADA;
    if (props.theme === 'colorSensitive' || props.theme === 'lexus') {
        linkButtonClassName = css.summaryEditLinkADA;
        flexBoxClassName = css.flexBoxADA;
        if (acctMode) {
            flexBoxClassName = css.flexBoxAcct;
        }
    }
    const summaryTitleClassName = classNames(
        css.summaryTitle,
        {
            [css.summaryTitleAudi]: props.theme === 'audi',
        },
    );
    const image = props.advisorPhotoPath !== ('' || undefined) ? props.advisorPhotoPath : '';
    let apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate + ' | ' + displayTime;

    let header = (<h4 className={summaryTitleClassName}>{strings.Appointment}</h4>);
    if (acctMode) {
        header = (<div className={css.dateRowA} id='dateTime'>{apptDate}</div>);
    } else if (editApptMode && props.modifyAppointment) {
        header = (
            <div style={{display: 'flex'}}>
                <h4 className={css.summaryTitle}>{strings.Appointment}</h4>
                <div onClick={props.modifyAppointment} className={linkButtonClassName}>{strings.ModifyAppointment}</div>
            </div>
        );
    }

    let lastLine = props.transIndex?.title;
    if (props.appointment.transFlag === 'N') {
        apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate;
        lastLine = strings.DropOff + displayTime;
    }
    const transRow = classNames (
        css.transRow,
        {
            [css.transRowLexus]: (props.theme === 'lexus'),
        },
    );

    const apptIcon = classNames (
        css.apptIcon,
        {
            [css.apptIconCadillac]: (props.theme === 'cadillac'),
        },
    );

    const renderAvatar = () => {
        if (image) {
            return (
                <Avatar
                    src={image}
                    elevation={0}
                    size={70}
                    color='blank'
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        if (acctMode && props.theme === 'kia') {
            return (
                <Avatar
                    src={NoProfile}
                    elevation={0}
                    size={70}
                    color='blank'
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        return (
            <div className={apptIcon}>
                <IconProfile2
                    style={{
                        height: '70px',
                        width: '70px',
                    }}
                />
            </div>
        );
    };

    return (
        <div>
            {header}
            <div className={css.summaryCard + ' ' + flexBoxClassName}>
                {renderAvatar()}
                <div className={css.appointmentData}>
                    {acctMode ?
                        (<div className={css.WithDiv}>{strings.With + ' '}</div>) : null
                    }
                    <div className={dateRowClassName} id='dateTime'>{apptDate}</div>
                    <div className={css.advRow} id='advisor'>{appointment.advName}</div>
                    <div className={transRow} id='transportation'>{lastLine}</div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentCard;
